import React, { useCallback, useEffect, useMemo, useState } from "react";
import Column from "./Column";
import { DeleteReasonSelect, IconWrapper, SubHeaderWrapper, UnifiedGrid, UnifiedGridWrapper, LoadMoreWrapper, EmptyState, KanbanContainer, KanbanColumn, KanbanColumnHeader, KanbanColumnCount, KanbanColumnBody } from "./styles";
import { LostStatuses } from "../../../types/LostStatuses";
import { Ticket as TicketType } from "../../../types";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/redux";
import { setBoardStatusFilter, setDeleteReason, setDeletionSource, setOpenColumn, setOpenedColumnType, clearTicketsByStatus } from "../../../redux/features/board/columnsSlice";
import { DislikeOutlined, MailOutlined } from "@ant-design/icons";

import { fetchTicketsByStatus, fetchTicketsByStatusBatch } from "../../../redux/features/board/columnsAsyncThunk";
import { fetchSchedulerInterval } from "../../../redux/features/schedulerSettings/schedulerSettingsAsyncThunk";
import { ReportType } from "../../../redux/features/board/columnTypes";
import { columnConfig, columnOrderConfig } from "./const";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { selectUserRole } from "../../../redux/features/auth/selectors";
import { checkHasPermission } from "../../../utils/helpers/checkHasPermission";
import { permissions } from "../../../utils/constants/permissions";
import { RequestResults } from "../../../redux/common/RequestStatus";
import { fetchDeleteReasons } from "../../../redux/features/deleteReason/deleteReasonAsyncThunk";
import { sortOptionsAlphabetically } from "../../../utils/helpers/sortOptionsAlphabetically";
import { FoundStatuses } from "../../../types/FoundItemTypes";
import { fetchFoundItemDiscardReasons } from "../../../redux/features/foundItemDiscardReason/foundItemDiscardReasonAsyncThunk";
import Tabs from "./Tabs";
import { Button, Flex, Skeleton, Spin } from "antd";
import TicketCard from "./Column/Ticket";
import { getStatusTheme } from "./statusThemes";
import BulkEmailModal from "../../EmailTemplates/BulkEmailModal";

const boardColumnList = columnConfig.filter((column) => column.isOnBoard && column.status !== undefined && column.status !== null);
// Cover ALL configured statuses (including off-board ones like NotPaid / DraftIncomplete / DraftCompleted)
// so the unified Unpaid view can show status pills for tickets across multiple statuses.
const boardStatusLabelMap = columnConfig.reduce((acc, column) => {
  if (column.status === undefined || column.status === null) return acc;
  const theme = getStatusTheme(column.status);
  acc[column.status] = theme.label || column.title;
  return acc;
}, {} as Record<number, string>);

const statusToPathMap: Record<number, string> = {
  [LostStatuses.Urgent]: "/reports/urgent",
  [LostStatuses.Open]: "/reports/open",
  [LostStatuses.Review]: "/reports/review",
  [LostStatuses.LFNotified]: "/reports/lf-notified",
  [LostStatuses.Deleted]: "/reports/deleted",
};

const Board: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const columns = useAppSelector((state) => state.columns.tickets);
  const openedColumn = useAppSelector((state) => state.columns.openedColumn);
  const establishment = useAppSelector((state) => state.users.establishment);
  const openedColumnType = useAppSelector((state) => state.columns.openedColumnType);
  const deleteReason = useAppSelector((state) => state.columns.deleteReason);
  const deletionSource = useAppSelector((state) => state.columns.deletionSource);
  const shipFilter = useAppSelector((state) => state.columns.shipFilter);
  const filters = useAppSelector((state) => state.columns.filters);
  const loadingByStatus = useAppSelector((state) => state.columns.loading);
  const offsets = useAppSelector((state) => state.columns.offsets);
  const totalCounts = useAppSelector((state) => state.columns.counts);
  const hasMoreByStatus = useAppSelector((state) => state.columns.hasMore);
  const boardStatusFilter = useAppSelector((state) => state.columns.boardStatusFilter);
  const { data: deleteReasonData, status } = useAppSelector((state) => state.deleteReason);
  const { data: discardReasonData, status: discardReasonStatus } = useAppSelector((state) => state.foundItemDiscardReason);
  const { id } = useParams();
  const { type } = useParams();
  const userRole = useAppSelector(selectUserRole);

  // Pull the scheduler config once on board mount so the Ticket cards know the configured
  // recent-payment highlight threshold (set in Settings). Cheap call; safe to dispatch even
  // when not used elsewhere on the page.
  useEffect(() => {
    dispatch(fetchSchedulerInterval());
  }, [dispatch]);

  // Handle URL-based filtering
  useEffect(() => {
    const pathToStatusMap: Record<string, number | "notPaid" | "drafts" | "unpaid" | null> = {
      "/reports": null,
      "/reports/urgent": LostStatuses.Urgent,
      "/reports/open": LostStatuses.Open,
      "/reports/pending": LostStatuses.Pending,
      "/reports/review": LostStatuses.Review,
      "/reports/in-process": LostStatuses.InProcess,
      "/reports/lf-notified": LostStatuses.LFNotified,
      // Unified Unpaid umbrella — all four URLs render the same page; the path
      // determines which top/sub tab is active. No separate pages anymore.
      "/reports/unpaid": "unpaid",
      "/reports/not-paid": "notPaid",
      "/reports/drafts": "drafts",
      "/reports/draft-incomplete": LostStatuses.DraftIncomplete,
      "/reports/draft-complete": LostStatuses.DraftCompleted,
      "/reports/overdue": LostStatuses.Overdue,
      "/reports/deleted": LostStatuses.Deleted,
    };

    const targetStatus = pathToStatusMap[location.pathname];

    // Only update if the target status is defined and different from current
    if (targetStatus !== undefined && boardStatusFilter !== targetStatus) {
      dispatch(setBoardStatusFilter(targetStatus));

      // Clear delete reason when switching views
      if (deleteReason !== "") {
        dispatch(setDeleteReason(""));
      }

      // Clear deletion-source bucket filter when switching views
      if (deletionSource !== "" && targetStatus !== LostStatuses.Deleted) {
        dispatch(setDeletionSource(""));
      }

      // Clear deleted tickets when navigating to deleted view to force refresh
      if (targetStatus === LostStatuses.Deleted) {
        dispatch(clearTicketsByStatus(LostStatuses.Deleted));
      }

      // Clear urgent tickets when navigating to urgent view to force fresh backend fetch
      // (syncUrgentTickets creates columns[6] as a side effect which prevents re-fetching)
      if (targetStatus === LostStatuses.Urgent) {
        dispatch(clearTicketsByStatus(LostStatuses.Urgent));
      }

      // Clear deleted tickets cache when navigating away from deleted view
      if (boardStatusFilter === LostStatuses.Deleted && targetStatus !== LostStatuses.Deleted) {
        dispatch(clearTicketsByStatus(LostStatuses.Deleted));
      }
    }
  }, [location.pathname, dispatch, deleteReason, boardStatusFilter]);

  useEffect(() => {
    if ([ReportType.Lost, ReportType.Found].includes(type as ReportType)) {
      if (id) {
        dispatch(setOpenColumn(+id));
        dispatch(setOpenedColumnType(type as ReportType));
        return;
      }
    }
    dispatch(setOpenedColumnType(undefined));
    dispatch(setOpenColumn(undefined));
  }, [type, id]);

  // Filter board columns based on the current view
  const isDraftSubFilter = boardStatusFilter === LostStatuses.DraftIncomplete || boardStatusFilter === LostStatuses.DraftCompleted;
  // Per-establishment kanban column visibility. Defaults mirror the entity defaults
  // so a fresh establishment sees Open / Review / InProcess / LFNotified / Urgent ON
  // and Deleted OFF without anyone touching Settings.
  const showOpen = (establishment as any)?.showOpenOnBoard ?? true;
  const showReview = (establishment as any)?.showReviewOnBoard ?? true;
  const showInProcess = (establishment as any)?.showInProcessOnBoard ?? true;
  const showLFNotified = establishment?.showLFNotifiedOnBoard ?? true;
  const showUrgent = (establishment as any)?.showUrgentOnBoard ?? true;
  const showDeleted = establishment?.showDeletedOnBoard ?? false;
  const paymentStatusFilter = useAppSelector((state) => state.columns.paymentStatusFilter);
  const isSearchActive = !!filters.searchValue;

  const PAID_STATUSES = useMemo(() => [
    LostStatuses.Open,
    LostStatuses.Pending,
    LostStatuses.Review,
    LostStatuses.InProcess,
    LostStatuses.LFNotified,
    LostStatuses.Urgent,
    LostStatuses.Overdue,
  ], []);
  const NOT_PAID_STATUSES = useMemo(() => [
    LostStatuses.NotPaid,
    LostStatuses.DraftIncomplete,
    LostStatuses.DraftCompleted,
  ], []);

  // Unpaid umbrella covers /reports/unpaid, /reports/not-paid, /reports/drafts and the
  // draft-incomplete / draft-complete sub-paths. On these pages, search is scoped to
  // unpaid statuses only — never the paid set.
  const isUnpaidUmbrella =
    boardStatusFilter === "unpaid" ||
    boardStatusFilter === "notPaid" ||
    boardStatusFilter === "drafts" ||
    boardStatusFilter === LostStatuses.DraftIncomplete ||
    boardStatusFilter === LostStatuses.DraftCompleted;

  // Effective payment scope for search results. Unpaid pages always search unpaid;
  // everywhere else the radio-driven paymentStatusFilter applies.
  const effectivePaymentScope: "paid" | "notPaid" = isUnpaidUmbrella ? "notPaid" : paymentStatusFilter;

  const boardColumns = useMemo(() => {
    // Universal search: fetch across the paid OR unpaid set based on the page context
    if (isSearchActive) {
      const targetStatuses = effectivePaymentScope === "paid" ? PAID_STATUSES : NOT_PAID_STATUSES;
      return columnConfig.filter(col => col.status !== undefined && col.status !== null && targetStatuses.includes(col.status));
    }
    // Unified Unpaid view (All): NotPaid + DraftIncomplete + DraftCompleted in one grid
    if (boardStatusFilter === "unpaid") {
      return columnConfig.filter(col =>
        col.status === LostStatuses.NotPaid ||
        col.status === LostStatuses.DraftIncomplete ||
        col.status === LostStatuses.DraftCompleted
      );
    }
    // Drafts view (all drafts or sub-filter): always fetch both columns for accurate counts
    if (boardStatusFilter === "drafts" || isDraftSubFilter) {
      return columnConfig.filter(col => col.status === LostStatuses.DraftIncomplete || col.status === LostStatuses.DraftCompleted);
    }
    // Not Paid view: fetch NotPaid (status 9) tickets directly
    if (boardStatusFilter === "notPaid") {
      return columnConfig.filter(col => col.status === LostStatuses.NotPaid);
    }
    // If we're filtering by a specific status (e.g., deleted, lf-notified), only show that column
    if (boardStatusFilter !== null && boardStatusFilter !== undefined) {
      // For statuses not on board (like LFNotified), get from full columnConfig
      const column = columnConfig.find(col => col.status === boardStatusFilter);
      if (column) {
        return [column];
      }
      return boardColumnList.filter(col => col.status === boardStatusFilter);
    }
    // Main reports page: include columns based on settings
    return boardColumnList.filter(col => {
      if (col.status === LostStatuses.LFNotified) return showLFNotified;
      if (col.status === LostStatuses.Deleted) return showDeleted;
      return true;
    });
  }, [boardStatusFilter, isDraftSubFilter, showLFNotified, showDeleted, isSearchActive, effectivePaymentScope, PAID_STATUSES, NOT_PAID_STATUSES]);

  const statusLabelMap = boardStatusLabelMap;

  // Kanban column order for main page. Each column is independently toggleable via
  // Settings → Board Settings. Open / Review / InProcess / LFNotified / Urgent default
  // ON; Deleted defaults OFF. InProcess sits between Review and LFNotified in the
  // new flow (Open → Review → InProcess → LFNotified).
  const kanbanColumns = useMemo(() => {
    if (boardStatusFilter !== null && boardStatusFilter !== undefined) return [];
    const order: number[] = [];
    if (showOpen) order.push(LostStatuses.Open);
    if (showReview) order.push(LostStatuses.Review);
    if (showInProcess) order.push(LostStatuses.InProcess);
    if (showLFNotified) order.push(LostStatuses.LFNotified);
    if (showDeleted) order.push(LostStatuses.Deleted);
    if (showUrgent) order.push(LostStatuses.Urgent);
    return order.map(status => {
      const config = columnConfig.find(c => c.status === status);
      const theme = getStatusTheme(status);
      return { status, config, theme };
    }).filter(c => c.config);
  }, [boardStatusFilter, showOpen, showReview, showInProcess, showLFNotified, showUrgent, showDeleted]);

  // Get tickets for a specific kanban column
  // "Refund window" check — payment within last N hours (configurable in Settings).
  // Cards inside the window are pushed to the bottom of each column so admins focus on the
  // actionable (past-window) ones at the top.
  const recentPaymentHighlightHours = useAppSelector(
    (state) => state.schedulerSettings.data.recentPaymentHighlightHours
  ) ?? 26;
  const isTicketInRefundWindow = useCallback((ticket: TicketType) => {
    if (!ticket.paymentDate || !recentPaymentHighlightHours || recentPaymentHighlightHours <= 0) return false;
    const ageHours = (Date.now() - new Date(ticket.paymentDate).getTime()) / (1000 * 60 * 60);
    return ageHours >= 0 && ageHours <= recentPaymentHighlightHours;
  }, [recentPaymentHighlightHours]);

  const getKanbanTickets = useCallback((status: number) => {
    const columnTickets = columns[status] || [];

    // Default sort (Review / LF Notified / Urgent / Deleted): actionable (past-window) on
    // top, refund-window cards at the bottom. Within each bucket, oldest payment first so
    // admins work the queue FIFO.
    const sortByRefundWindow = (list: TicketType[]) =>
      [...list].sort((a, b) => {
        const aInWindow = isTicketInRefundWindow(a);
        const bInWindow = isTicketInRefundWindow(b);
        if (aInWindow !== bInWindow) return aInWindow ? 1 : -1;
        const aTime = a.paymentDate ? new Date(a.paymentDate).getTime() : Number.POSITIVE_INFINITY;
        const bTime = b.paymentDate ? new Date(b.paymentDate).getTime() : Number.POSITIVE_INFINITY;
        return aTime - bTime;
      });

    // Open column has the INVERSE layout: cards still inside the Recent Payment Highlight
    // window go to the TOP, with the ones closest to the window expiring at the very top
    // (so admins see "this one will be actionable in N minutes" first). Past-window cards
    // sit below, still oldest-paid first (FIFO). This matches the admin's mental model of
    // working the Open column: peek at what's about to unlock, then handle the rest.
    const sortOpenColumn = (list: TicketType[]) =>
      [...list].sort((a, b) => {
        const aInWindow = isTicketInRefundWindow(a);
        const bInWindow = isTicketInRefundWindow(b);
        if (aInWindow !== bInWindow) return aInWindow ? -1 : 1; // in-window FIRST for Open
        // Within either bucket, oldest paymentDate first. For the in-window bucket this
        // surfaces "about to expire" at top; for the past-window bucket this is FIFO.
        const aTime = a.paymentDate ? new Date(a.paymentDate).getTime() : Number.POSITIVE_INFINITY;
        const bTime = b.paymentDate ? new Date(b.paymentDate).getTime() : Number.POSITIVE_INFINITY;
        return aTime - bTime;
      });

    if (status === LostStatuses.Deleted) {
      return sortByRefundWindow(columnTickets);
    }
    // For main board statuses, only show paid non-draft items
    if (status === LostStatuses.Urgent) {
      return sortByRefundWindow(columnTickets.filter((t) => (t.isUrgent || t.status === LostStatuses.Urgent) && t.isPaymentSuccess && !t.isDraft));
    }
    if (status === LostStatuses.Open) {
      return sortOpenColumn(columnTickets.filter((t) => t.status === status && t.isPaymentSuccess && !t.isDraft));
    }
    return sortByRefundWindow(columnTickets.filter((t) => t.status === status && t.isPaymentSuccess && !t.isDraft));
  }, [columns, isTicketInRefundWindow]);

  useEffect(() => {
    if (openedColumn) {
      return;
    }

    // Collect columns that need fetching
    const columnsToFetch = boardColumns.filter((columnConfigItem) => {
      const statusKey = columnConfigItem.status;
      return !(statusKey in columns) && !loadingByStatus[statusKey];
    });

    if (columnsToFetch.length === 0) return;

    // Smaller initial page size for kanban — first paint matters more than completeness.
    // Each card costs ~80–100ms server-side (per-row subquery aggregations for items,
    // payment, LF answers, etc.), so cutting the first page in half roughly halves the
    // wall-clock to first paint. Infinite scroll fills in the rest as the admin scrolls.
    // Larger pool for the universal-search flat view since it pools matches across statuses.
    const initialLimit = isSearchActive ? 100 : 15;
    // Use batch endpoint for kanban view (multiple unfetched columns) — single API call
    if (columnsToFetch.length > 1) {
      const batchColumns = columnsToFetch.map((columnConfigItem) => {
        const columnType = columnConfigItem.type ?? ReportType.Lost;
        const order = columnOrderConfig[`${columnConfigItem.status}-${columnType}`];
        return { status: columnConfigItem.status, offset: 0, limit: initialLimit, order, type: columnType };
      });
      const batchFilters: any = { ...filters };
      if (shipFilter !== null && shipFilter !== undefined) batchFilters.isDelivered = shipFilter;
      if (deleteReason && deleteReason !== "") batchFilters.reason = deleteReason;
      dispatch(fetchTicketsByStatusBatch({ columns: batchColumns, filters: batchFilters }));
    } else {
      // Single column — use existing individual endpoint
      const columnConfigItem = columnsToFetch[0];
      const statusKey = columnConfigItem.status;
      const columnType = columnConfigItem.type ?? ReportType.Lost;
      const order = columnOrderConfig[`${statusKey}-${columnType}`];
      const fetchParams: any = { status: statusKey, offset: 0, limit: initialLimit, filters, type: columnType, order, shipFilter };
      if (deleteReason && deleteReason !== "") fetchParams.reason = deleteReason;
      if (deletionSource) fetchParams.deletionSource = deletionSource;
      dispatch(fetchTicketsByStatus(fetchParams));
    }
  }, [boardColumns, columns, loadingByStatus, filters, deleteReason, deletionSource, shipFilter, openedColumn, dispatch]);

  const allTickets = useMemo(() => {
    const seen = new Set<string>();
    return boardColumns.reduce((acc, columnItem) => {
      const columnTickets = columns[columnItem.status] || [];
      if (columnTickets?.length) {
        columnTickets.forEach((ticket) => {
          if (!ticket) return;
          const uniqueKey = ticket.id || ticket.trackingId || `${ticket.status}-${ticket.createdAt || ""}`;
          if (!seen.has(uniqueKey)) {
            seen.add(uniqueKey);
            acc.push(ticket);
          }
        });
      }
      return acc;
    }, [] as TicketType[]);
  }, [boardColumns, columns]);

  const filteredTickets = useMemo(() => {
    // Universal search across all statuses, scoped by page context (paid vs unpaid)
    if (isSearchActive) {
      if (effectivePaymentScope === "paid") {
        return allTickets.filter((ticket) => ticket.isPaymentSuccess && !ticket.isDraft && ticket.status !== LostStatuses.Deleted);
      }
      return allTickets.filter((ticket) => (!ticket.isPaymentSuccess || ticket.isDraft) && ticket.status !== LostStatuses.Deleted);
    }
    if (boardStatusFilter === null || boardStatusFilter === undefined) {
      // On main reports page, exclude deleted items, unpaid claims, and drafts (show only paid claims)
      return allTickets.filter((ticket) => ticket.status !== LostStatuses.Deleted && ticket.isPaymentSuccess && !ticket.isDraft);
    }
    if (boardStatusFilter === "unpaid") {
      // Unified unpaid view: combines NotPaid + Draft Incomplete + Draft Complete
      return allTickets.filter((ticket) =>
        ticket.status === LostStatuses.NotPaid ||
        ticket.status === LostStatuses.DraftIncomplete ||
        ticket.status === LostStatuses.DraftCompleted
      );
    }
    if (boardStatusFilter === "drafts") {
      // All drafts (both incomplete and complete)
      return allTickets.filter((ticket) => ticket.status === LostStatuses.DraftIncomplete || ticket.status === LostStatuses.DraftCompleted);
    }
    if (boardStatusFilter === LostStatuses.DraftIncomplete || boardStatusFilter === LostStatuses.DraftCompleted) {
      // Specific draft sub-filter
      return allTickets.filter((ticket) => ticket.status === boardStatusFilter);
    }
    if (boardStatusFilter === "notPaid") {
      return allTickets.filter((ticket) => ticket.status === LostStatuses.NotPaid);
    }
    if (boardStatusFilter === LostStatuses.Urgent) {
      return allTickets.filter((ticket) => (ticket.isUrgent || ticket.status === LostStatuses.Urgent) && ticket.isPaymentSuccess && !ticket.isDraft);
    }
    // For all other status views, only show paid claims (except deleted view)
    if (boardStatusFilter === LostStatuses.Deleted) {
      // Match by deletedAt rather than status: cron-auto-deleted rows keep their original
      // workflow status (Open/Review/...) but have deletedAt set. Manual deletes flip status to Deleted.
      return allTickets.filter((ticket) => !!ticket.deletedAt);
    }
    return allTickets.filter((ticket) => ticket.status === boardStatusFilter && ticket.isPaymentSuccess && !ticket.isDraft);
  }, [allTickets, boardStatusFilter, isSearchActive, effectivePaymentScope]);

  const sortedTickets = useMemo(() => {
    // No client-side re-sort. Cards are displayed in the exact order they arrive
    // from the backend (which orders by columnOrderConfig[<status>-<type>] — urgent
    // first, then earliest paid). Redux appends "Load more" pages to the end of the
    // existing array (columnsSlice.ts line 282), so already-displayed cards stay
    // exactly where they are and new ones land at the bottom.
    //
    // Why we don't re-sort here anymore: the previous logic re-shuffled the full
    // list on every state change, so clicking "Load more" could move cards that
    // were already on screen — confusing if the admin was already mid-action on
    // one of them. Trusting the backend order keeps display stable.
    return filteredTickets;
  }, [filteredTickets]);

  const isAnyStatusLoading = useMemo(() => boardColumns.some((columnItem) => loadingByStatus[columnItem.status]), [boardColumns, loadingByStatus]);
  const allStatusesInitialized = useMemo(() => boardColumns.every((columnItem) => {
    const statusKey = columnItem.status;
    // A column is initialized if it exists in the columns object (even if empty)
    return columns[statusKey] !== undefined;
  }), [boardColumns, columns]);
  const isInitialGridLoading = sortedTickets.length === 0 && (isAnyStatusLoading || !allStatusesInitialized);
  const hasMoreTickets = useMemo(() => boardColumns.some((columnItem) => hasMoreByStatus[columnItem.status]), [boardColumns, hasMoreByStatus]);
  const isLoadMoreInProgress = sortedTickets.length > 0 && boardColumns.some((columnItem) => loadingByStatus[columnItem.status]);

  const loadMoreTickets = () => {
    boardColumns.forEach((columnItem) => {
      const statusKey = columnItem.status;
      if (hasMoreByStatus[statusKey] && !loadingByStatus[statusKey]) {
        const offset = offsets[statusKey] || 0;
        const columnType = columnItem.type ?? ReportType.Lost;
        const order = columnOrderConfig[`${statusKey}-${columnType}`];
        // Only pass reason if it's actually set (not empty string)
        const fetchParams: any = {
          status: statusKey,
          offset,
          limit: 10,
          filters,
          type: columnType,
          order,
          shipFilter
        };
        if (deleteReason && deleteReason !== "") {
          fetchParams.reason = deleteReason;
        }
        if (deletionSource) {
          fetchParams.deletionSource = deletionSource;
        }
        dispatch(fetchTicketsByStatus(fetchParams));
      }
    });
  };

  const loadMoreForColumn = useCallback((statusKey: number) => {
    if (hasMoreByStatus[statusKey] && !loadingByStatus[statusKey]) {
      const colConfig = columnConfig.find(c => c.status === statusKey);
      const columnType = colConfig?.type ?? ReportType.Lost;
      const order = columnOrderConfig[`${statusKey}-${columnType}`];
      const fetchParams: any = {
        status: statusKey,
        offset: offsets[statusKey] || 0,
        limit: 10,
        filters,
        type: columnType,
        order,
        shipFilter
      };
      if (deleteReason && deleteReason !== "") {
        fetchParams.reason = deleteReason;
      }
      if (deletionSource) {
        fetchParams.deletionSource = deletionSource;
      }
      dispatch(fetchTicketsByStatus(fetchParams));
    }
  }, [hasMoreByStatus, loadingByStatus, offsets, filters, deleteReason, deletionSource, shipFilter, dispatch]);

  const handleColumnScroll = useCallback((e: React.UIEvent<HTMLDivElement>, statusKey: number) => {
    const target = e.currentTarget;
    const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 100;
    if (nearBottom) {
      loadMoreForColumn(statusKey);
    }
  }, [loadMoreForColumn]);

  const columnTitle = useMemo(() => {
    if (openedColumn === FoundStatuses.Discarded && openedColumnType === ReportType.Found) {
      return "Discarded Found Items";
    }
    if (openedColumn) {
      const colIndex = columnConfig.findIndex((config) => config?.status === openedColumn);
      return columnConfig?.[colIndex]?.title || "Not Found Column";
    }
    return null;
  }, [openedColumn, columnConfig, openedColumnType]);

  const columnStatus = useMemo(() => {
    if (openedColumn === FoundStatuses.Discarded && openedColumnType === ReportType.Found) {
      return FoundStatuses.Discarded;
    }
    if (openedColumn) {
      const colIndex = columnConfig.findIndex((config) => config?.status === openedColumn);
      return columnConfig?.[colIndex]?.status || -1;
    }
    return 0;
  }, [openedColumn, columnConfig, openedColumnType]);

  const columnIcon = useMemo(() => {
    if (openedColumn === FoundStatuses.Discarded && openedColumnType === ReportType.Found) {
      return columnConfig.find((config) => config?.status === FoundStatuses.Discarded && config?.type === ReportType.Found)?.icon;
    }
    if (openedColumn) {
      const colIndex = columnConfig.findIndex((config) => config?.status === openedColumn);
      return columnConfig?.[colIndex]?.icon || <IconWrapper borderColor="#597EF7" backgroundColor="#D6E4FF" ><DislikeOutlined /></IconWrapper>;
    }
    return null;
  }, [openedColumn, columnConfig, openedColumnType]);

  const columnType = useMemo(() => {
    if (openedColumn === FoundStatuses.Discarded && openedColumnType === ReportType.Found) {
      return ReportType.Found;
    }
    if (openedColumn) {
      const colIndex = columnConfig.findIndex((config) => config?.status === openedColumn);
      return columnConfig?.[colIndex]?.type;
    }
    return undefined;
  }, [openedColumn, columnConfig, openedColumnType]);

  const hasPermission = useCallback(
    (permissionKey: string) => checkHasPermission(userRole, permissionKey),
    [userRole]
  );

  const isDiscardFoundStatus = useMemo(() => Number(id) === FoundStatuses.Discarded && type === ReportType.Found, [id, type]);
  const isDeleteLostStatus = useMemo(() => Number(id) === LostStatuses.Deleted && type === ReportType.Lost, [id, type]);
  const deleteReasonOptions = useMemo(() => {
    if (isDiscardFoundStatus) {
      return discardReasonData?.map((deleteReason) => ({
        label: deleteReason.reason ?? "",
        value: deleteReason.reason ?? "",
        key: deleteReason.id?.toString() ?? "",
      }));
    }

    return deleteReasonData?.map((deleteReason) => ({
      label: deleteReason.reason ?? "",
      value: deleteReason.reason ?? "",
      key: deleteReason.id?.toString() ?? "",
    }));
  }, [deleteReasonData, isDeleteLostStatus, isDiscardFoundStatus, discardReasonData]);


  // Fetch delete reasons when on deleted page (either via route params or direct URL)
  const shouldFetchDeleteReasons = isDeleteLostStatus || location.pathname === "/reports/deleted";

  useEffect(() => {
    if (status === RequestResults.idle && shouldFetchDeleteReasons) {
      dispatch(fetchDeleteReasons());
    }
  }, [dispatch, status, shouldFetchDeleteReasons]);

  useEffect(() => {
    if (discardReasonStatus === RequestResults.idle && isDiscardFoundStatus) {
      dispatch(fetchFoundItemDiscardReasons());
    }
  }, [dispatch, discardReasonStatus, isDiscardFoundStatus]);

  // Determine if tabs should be shown
  const shouldShowTabs = useMemo(() => {
    // Don't show tabs when a column is opened
    if (openedColumn) {
      return false;
    }
    // Show tabs on all reports pages (including not-paid, lf-notified, deleted)
    return location.pathname.startsWith("/reports");
  }, [location.pathname, openedColumn]);

  return (
    <>
      {shouldShowTabs && <Tabs />}
      <Flex wrap gap="small" align="center" justify="flex-end">
        {((openedColumn && (isDiscardFoundStatus || isDeleteLostStatus)) || location.pathname === "/reports/deleted") && <SubHeaderWrapper>
          {location.pathname === "/reports/deleted" && (
            <DeleteReasonSelect
              value={deletionSource ? deletionSource : undefined}
              allowClear
              placeholder="Filter by Deletion Source"
              options={[
                { label: "Manually Deleted", value: "manual" },
                { label: "Auto: Not Paid", value: "auto-unpaid" },
                { label: "Auto: Incomplete Draft", value: "auto-draft" },
              ]}
              onChange={(value) => dispatch(setDeletionSource((value as "" | "manual" | "auto-unpaid" | "auto-draft") || ""))}
              style={{ marginRight: 8 }}
            />
          )}
          <DeleteReasonSelect value={deleteReason ? deleteReason : undefined} allowClear
            placeholder={`Filter by Reason for ${isDiscardFoundStatus ? "Discard" : "Delete"}`}
            options={sortOptionsAlphabetically(deleteReasonOptions, "label")}
            onChange={(value) => dispatch(setDeleteReason(value as string))} />
        </SubHeaderWrapper>}

        {boardStatusFilter === "notPaid" && hasPermission(permissions.SendBulkEmail) && (
          <Button icon={<MailOutlined />} type="primary" onClick={() => setShowBulkEmailModal(true)}>
            Bulk Email
          </Button>
        )}
      </Flex>
      {openedColumn ? (
        <Column key={columnStatus} status={columnStatus} title={columnTitle ?? ""} icon={columnIcon} type={columnType} />
      ) : isSearchActive ? (
        /* Universal search results: flat grid across all paid (or not-paid) statuses */
        <UnifiedGridWrapper>
          {isInitialGridLoading ? (
            <UnifiedGrid>
              {Array.from({ length: 10 }).map((_, index) => (
                <div key={`search-skel-${index}`} style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: 12, background: "#fff" }}>
                  <Skeleton active paragraph={{ rows: 5 }} />
                </div>
              ))}
            </UnifiedGrid>
          ) : sortedTickets.length > 0 ? (
            <>
              <UnifiedGrid>
                {sortedTickets.map((ticket, index) => (
                  <TicketCard
                    key={ticket.id || `${ticket.status}-${index}`}
                    ticket={ticket}
                    index={index}
                    columnStatus={ticket.status}
                    forceFlatView
                    showStatusPill
                    statusLabel={statusLabelMap[ticket.status]}
                  />
                ))}
              </UnifiedGrid>
              {hasMoreTickets && (
                <LoadMoreWrapper>
                  {isLoadMoreInProgress ? (
                    <Spin />
                  ) : (
                    <Button type="default" onClick={loadMoreTickets}>
                      Load more
                    </Button>
                  )}
                </LoadMoreWrapper>
              )}
            </>
          ) : (
            <EmptyState>No matching {effectivePaymentScope === "paid" ? "paid" : "unpaid"} claims found</EmptyState>
          )}
        </UnifiedGridWrapper>
      ) : boardStatusFilter === null || boardStatusFilter === undefined ? (
        /* Kanban board view for main reports page */
        <KanbanContainer>
          {kanbanColumns.map(({ status, config, theme }) => {
            const kanbanTickets = getKanbanTickets(status);
            const isLoading = loadingByStatus[status];
            const isCollapsed = !isLoading && kanbanTickets.length === 0 && columns[status] !== undefined;

            // Review's column theme is now stable amber — the dynamic amber→green flip is
            // gone because the "green / actionable" cards no longer live in Review. They auto-
            // move to InProcess (which is permanently green-themed). Keeping Review amber
            // signals "still inside the hold-off window, wait" without admins second-guessing.
            const columnTheme = theme;
            const headerIcon = config!.icon;

            return (
              <KanbanColumn key={status} $borderColor={columnTheme.border} $collapsed={isCollapsed}>
                <KanbanColumnHeader
                  $backgroundColor={columnTheme.background}
                  $collapsed={isCollapsed}
                  style={{ cursor: isCollapsed ? "default" : "pointer" }}
                  onClick={() => {
                    if (isCollapsed) return;
                    const path = statusToPathMap[status];
                    if (path) navigate(path);
                  }}
                >
                  {headerIcon}
                  <span>{columnTheme.label}</span>
                  <KanbanColumnCount $color={columnTheme.pillColor}>
                    {totalCounts[status] ?? kanbanTickets.length}
                  </KanbanColumnCount>
                </KanbanColumnHeader>
                {!isCollapsed && (
                  <KanbanColumnBody onScroll={(e) => handleColumnScroll(e, status)}>
                    {isLoading && kanbanTickets.length === 0 ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <div key={`skel-${status}-${i}`} style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: 12, background: "#fff" }}>
                          <Skeleton active paragraph={{ rows: 3 }} />
                        </div>
                      ))
                    ) : (
                      kanbanTickets.map((ticket, index) => (
                        <TicketCard
                          key={ticket.id || `${ticket.status}-${index}`}
                          ticket={ticket}
                          index={index}
                          columnStatus={status}
                          forceFlatView
                          showStatusPill={status === LostStatuses.Urgent}
                          statusLabel={statusLabelMap[ticket.status]}
                        />
                      ))
                    )}
                    {isLoading && kanbanTickets.length > 0 && (
                      <div style={{ display: "flex", justifyContent: "center", padding: 8 }}>
                        <Spin size="small" />
                      </div>
                    )}
                  </KanbanColumnBody>
                )}
              </KanbanColumn>
            );
          })}
        </KanbanContainer>
      ) : (
        /* Flat grid view for filtered/sub-page views */
        <UnifiedGridWrapper>
          {isInitialGridLoading ? (
            <UnifiedGrid>
              {Array.from({ length: 10 }).map((_, index) => (
                <div key={`ticket-skeleton-${index}`} style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: 12, background: "#fff" }}>
                  <Skeleton active paragraph={{ rows: 5 }} />
                </div>
              ))}
            </UnifiedGrid>
          ) : sortedTickets.length > 0 ? (
            <>
              <UnifiedGrid>
                {sortedTickets.map((ticket, index) => (
                  <TicketCard
                    key={ticket.id || `${ticket.status}-${index}`}
                    ticket={ticket}
                    index={index}
                    columnStatus={ticket.status}
                    forceFlatView
                    showStatusPill
                    statusLabel={statusLabelMap[ticket.status]}
                  />
                ))}
              </UnifiedGrid>
              {hasMoreTickets && (
                <LoadMoreWrapper>
                  {isLoadMoreInProgress ? (
                    <Spin />
                  ) : (
                    <Button type="default" onClick={loadMoreTickets}>
                      Load more
                    </Button>
                  )}
                </LoadMoreWrapper>
              )}
            </>
          ) : (
            <EmptyState>No tickets found</EmptyState>
          )}
        </UnifiedGridWrapper>
      )}

      <BulkEmailModal open={showBulkEmailModal} onClose={() => setShowBulkEmailModal(false)} />
    </>
  );
};

export default Board;
